<template>
    <div class="d-flex flex-column page-wrapper identification">
        <page-header>
            <span v-if="jointBorrower == false"><span v-if="editMode">{{ $t("message.edit" )}}</span> {{ $t("message.identification" )}}</span>
            <span v-if="jointBorrower == true"><span v-if="editMode">{{ $t("message.edit" )}}</span> {{ $t("message.primaryApplicantIdentification" )}}</span>
        </page-header>
        <div class="container d-flex flex-column align-items-center page-container">
            <div class="d-flex flex-row justify-content-around steps-container">
                <div class="dot current-dot"></div><!-- Identification -->
                <div class="dot next-dot"></div><!-- Contact Info -->
                <div class="dot next-dot"></div><!-- Other Info -->
                <div class="dot next-dot" v-if="jointBorrower == true"/><!-- Joint Identification -->
                <div class="dot next-dot" v-if="jointBorrower == true"/><!-- Joint Contact Info -->
                <div class="dot next-dot" v-if="jointBorrower == true"/><!-- Joint Other Info -->
                <div class="dot next-dot"></div><!-- Accounts -->
                <div class="dot next-dot"></div><!-- Funding -->
                <div class="dot next-dot"></div><!-- Review -->
            </div>
            <p class="explanation-text">
                We do need a form of identification. If you select a Driver's License, you can upload a 
                picture of the front and back of your license and we'll use it to populate some information 
                in this application (which means less work for you).
            </p>
            <div class="d-flex flex-column justify-content-start align-items-start form-container">
                <b-form @submit.prevent="next" novalidate>
                    <b-form-group class="form-label" label="Select the type of ID you wish to use">
                        <b-button-group vertical class="btn-width" :id="$id('idtypes')">
                            <b-button class="btn" variant="outline-primary" :pressed="opt.value === selectedIdType" @click="selectIdType(opt)" v-for="opt in identificationTypeOptions" :key="opt.value">{{ opt.name }}</b-button>   
                        </b-button-group>
                    </b-form-group>
                    <div class="d-flex flex-column justify-content-center align-items-center margin-top-10 upload-btn-container" v-if="selectedIdType === 'DL' || selectedIdType === 'SI' || selectedIdType === 'PN'">
                        <img :src="dlFrontImage" v-if="dlFrontImage" :id="$id('frontimage')" />     
                        <input type="file" ref="frontFile" :id="$id('frontFile')" class="display-none" @change="onDlFrontFileChange" />
                        <b-button variant="primary" :id="$id('addfrontimage')" @click.prevent="$refs.frontFile.click()" class="d-flex justify-content-center align-items-center btn-width" v-if="!dlFrontImage">
                            <font-awesome-icon :icon="['fas', 'camera']"></font-awesome-icon>
                            <span class="margin-left-10" v-if="selectedIdType === 'DL'">{{ $t("message.driverLicenseFront" )}}</span>
                            <span class="margin-left-10" v-if="selectedIdType === 'SI'">{{ $t("message.idFront" )}}</span>
                            <span class="margin-left-10" v-if="selectedIdType === 'PN'">{{ $t("message.passportPicture" )}}</span>
                        </b-button>
                        <div class="field-error-message form-label margin-bottom-10" v-if="!$v.dlFrontImage.required && $v.dlFrontImage.$dirty">Please upload the front image of your ID</div>                                             
                        <b-button variant="dark" :id="$id('removefrontimage')" @click.prevent="removeFrontImage()" class="d-flex justify-content-center align-items-center btn-width" v-if="dlFrontImage">
                            <font-awesome-icon :icon="['fas', 'times']"></font-awesome-icon>
                            <span class="margin-left-10">{{ $t("message.remove" )}}</span>
                        </b-button>                                            
                    </div>
                    <div class="d-flex flex-column justify-content-center align-items-center upload-btn-container" v-if="selectedIdType === 'DL' || selectedIdType === 'SI'">
                        <img :src="dlBackImage" v-if="dlBackImage" :id="$id('backimage')" />     
                        <input type="file" ref="backFile" :id="$id('backFile')" class="display-none" @change="onDlBackFileChange" />
                        <b-button variant="primary" :id="$id('addbackimage')" @click.prevent="$refs.backFile.click()" class="d-flex justify-content-center align-items-center btn-width" v-if="!dlBackImage && !parsingIdentification">
                            <font-awesome-icon :icon="['fas', 'camera']"></font-awesome-icon>
                            <span class="margin-left-10" v-if="selectedIdType === 'DL'">{{ $t("message.driverLicenseBack" )}}</span>
                            <span class="margin-left-10" v-if="selectedIdType === 'SI'">{{ $t("message.idBack" )}}</span>
                        </b-button>
                        <div class="field-error-message form-label margin-bottom-10" v-if="!$v.dlBackImage.required && $v.dlBackImage.$dirty">Please upload the back image of your ID</div>
                        <div v-if="parsingIdentification" class="d-flex flex-column align-items-center margin-top-15">
                            <p class="processing-text">{{ $t("message.readingYourId" )}}</p>
                            <img src="@/assets/img/light/activity.gif" class="processing-img-interim" />
                        </div>                                                
                        <b-button variant="dark" :id="$id('removebackimage')" @click.prevent="removeBackImage()" class="d-flex justify-content-center align-items-center btn-width" v-if="dlBackImage && !parsingIdentification">
                            <font-awesome-icon :icon="['fas', 'times']"></font-awesome-icon>
                            <span class="margin-left-10">{{ $t("message.remove" )}}</span>
                        </b-button>
                    </div>
                    <div class="d-flex flex-column margin-top-20" v-if="selectedIdType !== '' && !parsingIdentification">
                        <b-form-group class="form-label">
                            <label>{{ selectedIdName }} Number</label>
                            <b-form-input type="text" :id="$id('dlNumber')" v-model="dlNumber"/>        
                            <div class="field-error-message" v-if="!$v.dlNumber.required && $v.dlNumber.$dirty">Please enter your {{ selectedIdName }} Number</div>             
                        </b-form-group>
                        <b-form-group class="form-label" label="Date Issued">
                            <datepicker v-model="dlDateIssued" :id="$id('dlDateIssued')" format="MM/dd/yyyy" :input-class="{ 'form-control': true }" name="dlDateIssued" :typeable="true" v-mask="'##/##/####'"></datepicker> 
                            <div class="field-error-message" v-if="!$v.dlDateIssued.required && $v.dlDateIssued.$dirty">Please enter the issue date of your  {{ selectedIdName }}</div>             
                            <div class="field-error-message" v-if="!$v.dlDateIssued.minValue && $v.dlDateIssued.$dirty">Please enter a valid issue date for your  {{ selectedIdName }}</div>             
                            <div class="field-error-message" v-if="!$v.dlDateIssued.maxValue && $v.dlDateIssued.$dirty">Your  {{ selectedIdName }} cannot be issued in the future</div>
                        </b-form-group>
                        <b-form-group class="form-label" label="Expiration Date">
                            <datepicker v-model="dlExpirationDate" :id="$id('dlExpirationDate')" format="MM/dd/yyyy" :input-class="{ 'form-control': true }" name="dlExpirationDate" :typeable="true" v-mask="'##/##/####'"></datepicker> 
                            <div class="field-error-message" v-if="!$v.dlExpirationDate.required && $v.dlExpirationDate.$dirty">Please enter the expiration date of your {{ selectedIdName }}</div>             
                            <div class="field-error-message" v-if="!$v.dlExpirationDate.minValue && $v.dlExpirationDate.$dirty">Your  {{ selectedIdName }} cannot be expired</div>             
                            <div class="field-error-message" v-if="!$v.dlExpirationDate.maxValue && $v.dlExpirationDate.$dirty">Please enter a valid expiration date for your {{ selectedIdName }}</div>  
                        </b-form-group>
                        <b-form-group class="form-label" label="Issuing State" v-if="selectedIdType === 'DL' || selectedIdType === 'SI'">
                            <b-form-select class="form-control" :id="$id('dlIssuingState')" v-model="dlIssuingState" :options="stateOptions"></b-form-select>                
                            <div class="field-error-message" v-if="!$v.dlIssuingState.required && $v.dlIssuingState.$dirty">Please enter the issuing state for your {{ selectedIdName }}</div>             
                        </b-form-group>
                        <b-form-group class="form-label" label="First Name">
                            <b-form-input type="text" :id="$id('firstName')" v-model="firstName"/>        
                            <div class="field-error-message" v-if="!$v.firstName.required && $v.firstName.$dirty">Please enter your First Name</div>        
                        </b-form-group>
                        <b-form-group class="form-label" label="Last Name">
                            <b-form-input type="text" :id="$id('lastName')" v-model="lastName"/>        
                            <div class="field-error-message" v-if="!$v.lastName.required && $v.lastName.$dirty">Please enter your Last Name</div>        
                        </b-form-group>
                        <b-form-group class="form-label" label="Date of Birth">
                            <datepicker v-model="dateOfBirth" :id="$id('dateOfBirth')" format="MM/dd/yyyy" :input-class="{ 'form-control': true }" name="dateOfBirth" :typeable="true" v-mask="'##/##/####'"></datepicker> 
                            <div class="field-error-message" v-if="!$v.dateOfBirth.required && $v.dateOfBirth.$dirty">Please enter your Date of Birth</div>        
                            <div class="field-error-message" v-if="!$v.dateOfBirth.minValue && $v.dateOfBirth.$dirty">Please enter a valid Date of Birth</div>        
                            <div class="field-error-message" v-if="!$v.dateOfBirth.maxValue && $v.dateOfBirth.$dirty">Your Date of Birth cannot be in the future</div>
                        </b-form-group>
                        <b-form-group class="form-label" label="Social Security Number">
                            <b-input-group>
                                <b-form-input v-model="taxId" :id="$id('taxId')" :type="showTaxId ? 'text' : 'password'" class="form-control" v-facade="'###-##-####'" placeholder="XXX-XX-XXXX" :masked="showTaxId"/>        
                                <b-input-group-append>
                                    <b-button variant="highlight" @click.prevent="showTaxId = !showTaxId">
                                        <span v-if="showTaxId"><font-awesome-icon icon="eye"></font-awesome-icon></span>
                                        <span v-if="!showTaxId"><font-awesome-icon icon="eye-slash"></font-awesome-icon></span>
                                    </b-button>
                                </b-input-group-append>
                                <div class="field-error-message" v-if="!$v.taxId.required && $v.taxId.$dirty">Please enter your Social Security Number</div>        
                                <div class="field-error-message" v-if="!$v.taxId.minLength && $v.taxId.$dirty">Please enter a valid Social Security Number</div>    
                            </b-input-group>
                        </b-form-group>
                    </div>
                    <div class="d-flex flex-column justify-content-between align-items-center final-action-div">
                        <b-button variant="secondary" type="submit" :id="$id('next')" class="btn btn-width" :disabled="selectedIdType === '' || parsingIdentification">
                            <span v-if="!editMode">{{ $t("message.next" )}}</span>
                            <span v-if="editMode">{{ $t("message.saveEdit" )}}</span>
                        </b-button>
                        <b-button variant="light" @click="back()" :id="$id('back')" class="btn btn-width">
                            <span v-if="!editMode">{{ $t("message.back" )}}</span>
                            <span v-if="editMode">{{ $t("message.cancel" )}}</span>
                        </b-button>
                    </div>
                </b-form>
            </div>
        </div>
        <page-footer />
        <b-modal ref="errorModalRef" body-text-variant="dark" header-text-variant="dark" centered :ok-only="true" ok-variant="secondary" title="Missing Information">{{ $t('message.missingRequiredFieldsMsg') }}</b-modal>
        <b-modal ref="driversLicenseModalRef" body-text-variant="dark" header-text-variant="dark" centered :ok-only="true" ok-variant="secondary" title="Missing Information">{{ $t('message.missingRequiredIdImageMsg') }}</b-modal>
        <b-modal ref="ageModalRef" body-text-variant="dark" header-text-variant="dark" centered :ok-only="true" ok-variant="secondary" title="Applicant Age">{{ $t('message.missingRequiredAgeMsg') }}</b-modal>
        <b-modal ref="moreInfoRequiredModalRef" body-text-variant="dark" header-text-variant="dark" centered hide-header-close no-close-on-esc title="Notice" @cancel="cancel" cancel-variant="outline-secondary" ok-title="Continue" ok-variant="secondary" @ok="gotoContactInfo">
            <p>Your identification information has changed and you may not be eligible for membership. We need a little more information to make sure you are able to join {{ creditUnionName }}.</p>
        </b-modal>
    </div>
</template>

<script>
import { required, requiredIf, minValue, maxValue, minLength, maxLength } from 'vuelidate/lib/validators';
import Datepicker from 'vuejs-datepicker';
import { mask } from 'vue-the-mask';
import { mapState, mapActions, mapMutations } from "vuex";
export default {
    name: "Identification",
    components: {
        Datepicker
    },
    directives: {
        mask
    },
    mounted() {
        this.editedIdentificationInfo = false;
    },
    data: function() {
        return {
            identificationTypeOptions: [
                { name: 'Driver\'s License', value: 'DL' },
                { name: 'US Passport', value: 'PN' },
                { name: 'State-Issued ID Card', value: 'SI' },
                { name: 'US Military ID', value: 'CU' },
            ],
            showTaxId: false,
        };
    },
    computed: {
        ...mapState([
            "editMode",
            "currentState",
            "stateOptions",
            "isJointApplication",
            "primaryIdentification",
            "primaryApplicant",
            "parsingIdentification",
            "requireIDBackImage",
            "requireIDFrontImage",
            'creditUnion'
        ]),
        creditUnionName: {
            get() {
                return this.creditUnion.name;
            }
        },
        jointBorrower: {
            get() {
                return this.isJointApplication;
            }
        },
        requireIDBackImage: {
            get() {
                if(this.selectedIdType === 'CU' || this.selectedIdType === 'PN') {
                    return false;
                } else {
                    return this.$store.state.requireIDBackImage;
                }
            }
        },
        requireIDFrontImage: {
            get() {
                if(this.selectedIdType === 'CU') {
                    return false;
                } else {
                    return this.$store.state.requireIDFrontImage;
                }
            }
        },
        selectedIdType: {         
            get(){
                return this.primaryIdentification.category;
            },
            set(value) {
                this.updateIdentificationCategory({ isPrimary: true, category: value.value });
            }
        },
        selectedIdName() {
            var self = this;
            var idType = this.identificationTypeOptions.find(function(x) {
                return x.value == self.selectedIdType;
            });
            return idType.name;
        },
        dlImageFormat: {
            get() {
                return this.primaryIdentification.imageFormat;
            },
            set(value) {
                this.updateIdentificationImageFormat({ isPrimary: true, imageFormat: value });
            } 
        },
        dlFrontImage: {
            get() {
                return this.primaryIdentification.image;
            },
            set(value) {
                this.updateIdentificationImage({ isPrimary: true, image: value });
            } 
        },
        dlBackImage: {
            get() {
                return this.primaryIdentification.backImage;
            },
            set(value) {
                if(value && value !== '') {
                    this.parseIdentificationBack({ imageData: value }).then((parsedIdentification) => {
                        this.updateIdentificationBackImage({ isPrimary: true, backImage: value });
                        this.updateApplicantFromIdParse({ isPrimary: true, idData: parsedIdentification });
                    });
                } else {
                    this.updateIdentificationBackImage({ isPrimary: true, backImage: value });
                }
            } 
        },
        dlNumber: {
            get() {
                return this.primaryIdentification.identificationNumber;
            }, 
            set(value) {
                this.updateIdentificationNumber({ isPrimary: true, identificationNumber: value });
            }
        },
        dlDateIssued: { 
            get() {
                return this.primaryIdentification.issueDate;
            }, 
            set(value) {
                this.updateIdentificationIssueDate({ isPrimary: true, issueDate: value });
            }
        },
        dlExpirationDate: {
            get() {
                return this.primaryIdentification.expirationDate;
            }, 
            set(value) {
                this.updateIdentificationExpirationDate({ isPrimary: true, expirationDate: value });
            }
        }, 
        dlIssuingState: {
            get() {
                return this.primaryIdentification.issuer;
            }, 
            set(value) {
                this.updateIdentificationIssuer({ isPrimary: true, issuer: value });
            }
        },
        firstName: {
            get() {
                return this.primaryApplicant.firstName;
            },
            set(value) {
                this.updateApplicantFirstName({ isPrimary: true, firstName: value });
            }
        },
        lastName: {
            get() {
                return this.primaryApplicant.lastName;
            },
            set(value) {
                this.updateApplicantLastName({ isPrimary: true, lastName: value });
            }
        },
        taxId: {
            get() {
                return this.primaryApplicant.taxId;
            },
            set(value) {
                this.updateApplicantTaxId({ isPrimary: true, taxId: value });
            }
        },
        dateOfBirth: {
            get() {
                return this.primaryApplicant.dateOfBirth;
            },
            set(value) {
                this.updateApplicantDateOfBirth({ isPrimary: true, dateOfBirth: value });
            }
        },
        identificationInfoChanged: {
            get() {
                var prevPrimaryApplicant = this.currentState.primaryApplicant;
                if(prevPrimaryApplicant.firstName !== this.primaryApplicant.firstName
                    || prevPrimaryApplicant.lastName !== this.primaryApplicant.lastName
                    || new Date(prevPrimaryApplicant.dateOfBirth).getTime() !== new Date(this.primaryApplicant.dateOfBirth).getTime()
                    || prevPrimaryApplicant.taxId !== this.primaryApplicant.taxId) {
                    return true;
                }
            }
        },
        editedIdentificationInfo: {
            get() {
                return this.$store.state.editedIdentificationInfo;
            },
            set(value) {
                this.updateEditedIdentificationInfo(value);
            }
        }
    },
    methods: {
        ...mapActions([
            "parseIdentificationBack",
        ]),
        ...mapMutations([
            "updateIdentificationCategory",
            "updateIdentificationImageFormat",
            "updateIdentificationImage",
            "updateIdentificationBackImage",
            "updateApplicantFromIdParse",
            "updateIdentificationNumber",
            "updateIdentificationIssueDate",
            "updateIdentificationExpirationDate",
            "updateIdentificationIssuer",
            "updateApplicantFirstName",
            "updateApplicantLastName",
            "updateApplicantTaxId",
            "updateApplicantDateOfBirth",
            'updateEditedIdentificationInfo'
        ]),
        selectIdType: function(opt) {
            //are you sure? unsave changes?
            this.$v.$reset();
            this.removeFrontImage();
            this.removeBackImage();
            this.selectedIdType = opt;
        },
        next: function() {
            this.$v.$touch();
            if(this.$v.$invalid) {
                this.$refs.errorModalRef.show();
            } else {
                if(this.editMode) {
                    if(this.identificationInfoChanged) {
                        this.$refs.moreInfoRequiredModalRef.show();
                    } else {
                        this.$router.push("review");
                    }
                } else {
                    this.$router.push("contactinformation");
                }
            }      
        },
        back() {
            if(this.editMode) {
                this.$store.replaceState(this.currentState);
                this.$router.push("review");
            } else {
                this.$router.push('/');
            }
        },
        cancel: function () {
            this.$refs.moreInfoRequiredModalRef.hide();
        },    
        gotoContactInfo: async function() {
            console.info('continue to contact information page, we are doing a multi-page edit');
            this.editedIdentificationInfo = true;
            this.$router.push("contactinformation");
        },
        onDlFrontFileChange: function(e) {
            var files = e.target.files || e.dateTransfer.files;        
            if(!files.length) {
                return;
            }
            this.createFrontImage(files[0]);
        },
        createFrontImage: function(file)  {
            var reader = new FileReader();
            var vm = this;
            reader.onload = (e) => {
                vm.dlFrontImage = e.target.result;       
                vm.dlImageFormat = file.type.split('/')[1];
            };
            reader.readAsDataURL(file);   
        },
        removeFrontImage: function() {
            this.dlImageFormat = '';
            this.dlFrontImage = '';
        },
        onDlBackFileChange: function(e) {
            var files = e.target.files || e.dateTransfer.files;        
            if(!files.length) {
                return;
            }
            this.createBackImage(files[0]);
        },
        createBackImage: function(file)  {        
            var reader = new FileReader();
            var vm = this;

            reader.onload = (e) => {
                vm.dlBackImage = e.target.result;                   
            };
            reader.readAsDataURL(file);   
        },
        removeBackImage: function() {        
            this.dlBackImage = '';
        }
    },
    validations: {
        dlBackImage:  {
            required: requiredIf(function () {
                return this.requireIDBackImage;
            })
        },
        dlFrontImage:  {
            required: requiredIf(function () {
                return this.requireIDFrontImage;
            })
        },
        dlNumber: {
            required
        },
        dlDateIssued: {
            required,
            minValue: minValue(new Date("1900-01-01")),
            maxValue: maxValue(new Date())
        },
        dlExpirationDate: {
            required,
            minValue: minValue(new Date()),
            maxValue: maxValue(new Date("2100-12-31"))
        },
        dlIssuingState:  {
            required: requiredIf(function () {
                return this.selectedIdType === 'SI' || this.selectedIdType === 'DL';
            })
        },
        firstName: {
            required        
        },
        lastName: {
            required
        },
        dateOfBirth: {
            required,
            minValue: minValue(new Date("1900-01-01")),
            maxValue: maxValue(new Date())
        },  
        taxId: {
            required,
            minLength: minLength(9),
            maxLength: maxLength(9), 
        }, 
    },
};
</script>